/**
 * API client functions for canonical composers management.
 */

import type {
  CanonicalComposer,
  CanonicalComposerDetailsResponse,
  CreateCanonicalComposerInput,
  ListCanonicalComposersResponse,
  PromoteCanonicalComposerInput,
  PromoteCanonicalComposerResponse,
  UpdateCanonicalComposerInput,
} from '../../types/canonical-composer'
import apiClient from './client'

/**
 * List all canonical composers with piece counts.
 */
export async function listCanonicalComposers(): Promise<CanonicalComposer[]> {
  const response = await apiClient.get<ListCanonicalComposersResponse>('/admin/canonical-composers')
  return response.canonical_composers
}

/**
 * Get canonical composer details with linked canonical pieces.
 */
export async function getCanonicalComposerDetails(
  canonicalComposerId: string,
): Promise<CanonicalComposerDetailsResponse> {
  return await apiClient.get<CanonicalComposerDetailsResponse>(
    `/admin/canonical-composers/${canonicalComposerId}`,
  )
}

/**
 * Create a new canonical composer.
 */
export async function createCanonicalComposer(
  input: CreateCanonicalComposerInput,
): Promise<CanonicalComposer> {
  return await apiClient.post<CanonicalComposer>('/admin/canonical-composers', input)
}

/**
 * Update a canonical composer.
 */
export async function updateCanonicalComposer(
  canonicalComposerId: string,
  input: UpdateCanonicalComposerInput,
): Promise<CanonicalComposer> {
  return await apiClient.put<CanonicalComposer>(
    `/admin/canonical-composers/${canonicalComposerId}`,
    input,
  )
}

/**
 * Delete a canonical composer.
 * Will fail if the composer has linked canonical pieces (piece_count > 0).
 */
export async function deleteCanonicalComposer(canonicalComposerId: string): Promise<void> {
  await apiClient.delete(`/admin/canonical-composers/${canonicalComposerId}`)
}

/**
 * Promote a canonical composer to a Wikidata reference.
 * Migrates all linked canonical pieces and suggestions to the Wikidata composer.
 */
export async function promoteCanonicalComposer(
  canonicalComposerId: string,
  input: PromoteCanonicalComposerInput,
): Promise<PromoteCanonicalComposerResponse> {
  return await apiClient.post<PromoteCanonicalComposerResponse>(
    `/admin/canonical-composers/${canonicalComposerId}/promote`,
    input,
  )
}
